import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { 
  Inbox, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Mail,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  ExternalLink,
  Plus,
  Settings,
  Search,
  Download,
  CheckSquare,
  Square,
  Zap,
  Play,
  Pause,
  X,
  StopCircle,
  Filter,
  ShoppingCart,
  TrendingUp
} from 'lucide-react';
import { ticketService } from '../../services/api';
import { toast } from 'sonner';

// Platform Manager Component
const PlatformManager = () => {
  const [builtinPlatforms, setBuiltinPlatforms] = useState([]);
  const [customPlatforms, setCustomPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPlatform, setNewPlatform] = useState({ name: '', emails: '', default_type: 'VENTE' });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadPlatforms();
  }, []);

  const loadPlatforms = async () => {
    setLoading(true);
    try {
      const [builtin, custom] = await Promise.all([
        ticketService.getBuiltinPlatforms(),
        ticketService.getCustomPlatforms()
      ]);
      setBuiltinPlatforms(builtin || []);
      setCustomPlatforms(custom || []);
    } catch (e) {
      console.error('Error loading platforms:', e);
    }
    setLoading(false);
  };

  const handleAddPlatform = async () => {
    if (!newPlatform.name || !newPlatform.emails) return;
    
    setSaving(true);
    setResult(null);
    
    try {
      // Parse emails (comma or newline separated)
      const emailList = newPlatform.emails
        .split(/[,\n]/)
        .map(e => e.trim().toLowerCase())
        .filter(e => e.includes('@'));
      
      if (emailList.length === 0) {
        setResult({ ok: false, msg: 'Ajoutez au moins un email valide' });
        setSaving(false);
        return;
      }
      
      const platform = {
        name: newPlatform.name.toUpperCase(),
        emails: emailList,
        keywords: [],
        default_type: newPlatform.default_type
      };
      
      const r = await ticketService.addCustomPlatform(platform);
      
      if (r.success) {
        setResult({ ok: true, msg: `Plateforme "${platform.name}" ajoutée !` });
        setNewPlatform({ name: '', emails: '', default_type: 'VENTE' });
        setShowAddForm(false);
        loadPlatforms();
      } else {
        setResult({ ok: false, msg: 'Erreur lors de l\'ajout' });
      }
    } catch (e) {
      setResult({ ok: false, msg: 'Erreur: ' + e.message });
    }
    setSaving(false);
  };

  const handleDeletePlatform = async (platformId, platformName) => {
    if (!window.confirm(`Supprimer la plateforme "${platformName}" ?`)) return;
    
    try {
      const r = await ticketService.deleteCustomPlatform(platformId);
      if (r.success) {
        setResult({ ok: true, msg: `Plateforme "${platformName}" supprimée` });
        loadPlatforms();
      }
    } catch (e) {
      setResult({ ok: false, msg: 'Erreur lors de la suppression' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-white font-medium">Gestion des Plateformes</h3>
        <Button 
          onClick={() => setShowAddForm(!showAddForm)}
          size="sm"
          className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
        >
          <Plus className="h-4 w-4 mr-1" />
          Nouvelle Plateforme
        </Button>
      </div>

      {/* Add Platform Form */}
      {showAddForm && (
        <div className="p-4 rounded-lg border border-[#C4A052]/30 bg-zinc-900/50 space-y-3">
          <p className="text-white text-sm font-medium">Ajouter une plateforme</p>
          
          <Input 
            placeholder="Nom (ex: OLIVIER GRUOT)" 
            value={newPlatform.name}
            onChange={e => setNewPlatform({ ...newPlatform, name: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
          />
          
          <Input 
            placeholder="Email(s) expéditeur (ex: oliviergruot@gmail.com)" 
            value={newPlatform.emails}
            onChange={e => setNewPlatform({ ...newPlatform, emails: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
          />
          <p className="text-xs text-zinc-500">Séparez plusieurs emails par des virgules</p>
          
          <div className="flex gap-2">
            <button
              onClick={() => setNewPlatform({ ...newPlatform, default_type: 'ACHAT' })}
              className={`flex-1 p-2 rounded border flex items-center justify-center gap-2 ${
                newPlatform.default_type === 'ACHAT' 
                  ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' 
                  : 'bg-zinc-800 border-zinc-700 text-zinc-400'
              }`}
            >
              <ShoppingCart className="h-4 w-4" />
              ACHAT
            </button>
            <button
              onClick={() => setNewPlatform({ ...newPlatform, default_type: 'VENTE' })}
              className={`flex-1 p-2 rounded border flex items-center justify-center gap-2 ${
                newPlatform.default_type === 'VENTE' 
                  ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                  : 'bg-zinc-800 border-zinc-700 text-zinc-400'
              }`}
            >
              <TrendingUp className="h-4 w-4" />
              VENTE
            </button>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAddForm(false)}
              className="flex-1 border-zinc-700"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleAddPlatform}
              disabled={saving || !newPlatform.name || !newPlatform.emails}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ajouter'}
            </Button>
          </div>
        </div>
      )}

      {/* Result Message */}
      {result && (
        <div className={`flex items-center gap-2 p-2 rounded text-sm ${
          result.ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
        }`}>
          {result.ok ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {result.msg}
        </div>
      )}

      {/* Custom Platforms */}
      {customPlatforms.length > 0 && (
        <div className="space-y-2">
          <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider">
            Plateformes personnalisées ({customPlatforms.length})
          </p>
          {customPlatforms.map(p => (
            <div key={p.id} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{p.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    p.default_type === 'ACHAT' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-purple-500/20 text-purple-400'
                  }`}>
                    {p.default_type}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {p.emails?.join(', ')}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeletePlatform(p.id, p.name)}
                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Built-in Platforms */}
      <div className="space-y-2">
        <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider">
          Plateformes intégrées ({builtinPlatforms.length})
        </p>
        <div className="grid grid-cols-2 gap-2">
          {builtinPlatforms.map(p => (
            <div key={p.source} className="p-2 rounded bg-zinc-800/30 border border-zinc-800">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white">{p.source}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  p.defaultType === 'ACHAT' 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : 'bg-purple-500/20 text-purple-400'
                }`}>
                  {p.defaultType}
                </span>
              </div>
              <p className="text-xs text-zinc-600 truncate mt-1">{p.emails?.[0]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Help text */}
      <p className="text-xs text-zinc-500 bg-zinc-800/30 p-2 rounded">
        💡 Les emails de ces expéditeurs seront automatiquement classés en ACHAT ou VENTE selon leur plateforme.
      </p>
    </div>
  );
};

const PROVIDERS = {
  gmail: { name: 'Gmail', icon: '📧', server: 'imap.gmail.com', url: 'https://myaccount.google.com/apppasswords' },
  outlook: { name: 'Hotmail/Outlook', icon: '📬', server: 'outlook.office365.com', url: 'https://account.microsoft.com/security' },
  yahoo: { name: 'Yahoo', icon: '📮', server: 'imap.mail.yahoo.com', url: 'https://login.yahoo.com/myaccount/security' },
  icloud: { name: 'iCloud', icon: '☁️', server: 'imap.mail.me.com', url: 'https://appleid.apple.com/account/manage' }
};

export const InboxModal = ({ open, onOpenChange, onRefreshData }) => {
  const [tab, setTab] = useState('tout');
  const [accounts, setAccounts] = useState([]);
  const [preview, setPreview] = useState([]);
  const [filteredPreview, setFilteredPreview] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [scanProgress, setScanProgress] = useState(0);
  const [shouldStop, setShouldStop] = useState(false);
  const abortControllerRef = useRef(null);
  
  // Search/filter state - now used for DIRECT IMAP search
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // Date filter
  const [dateFilter, setDateFilter] = useState('');
  
  // Auto-sync state
  const [autoSync, setAutoSync] = useState(false);
  const [autoSyncInterval, setAutoSyncInterval] = useState(60); // seconds
  const autoSyncTimer = useRef(null);
  const [lastSync, setLastSync] = useState(null);
  
  // Add account form
  const [showAddForm, setShowAddForm] = useState(false);
  const [provider, setProvider] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const loadAccounts = async () => {
    try {
      const accs = await ticketService.getEmailAccounts();
      setAccounts(accs || []);
    } catch (e) {
      console.error(e);
    }
  };

  // FAST search - uses keyword directly on IMAP server
  // Real progress animation from 0 to 100%
  const loadPreview = async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setScanProgress(0);
      setShouldStop(false);
    }
    setResult(null);
    setSelectedEmails(new Set());
    
    // Animate progress realistically
    let progress = 0;
    let progressInterval = null;
    
    if (!silent) {
      progressInterval = setInterval(() => {
        progress += Math.random() * 8 + 2; // Random increment 2-10%
        if (progress > 85) progress = 85; // Cap at 85% until complete
        setScanProgress(Math.round(progress));
      }, 200);
    }
    
    try {
      // Pass keyword directly to backend for IMAP SEARCH (FAST!)
      const r = await ticketService.previewEmails(
        500, 
        dateFilter || null, 
        searchKeyword.trim() || null
      );
      
      // Clear interval and jump to 100%
      if (progressInterval) clearInterval(progressInterval);
      if (!silent) setScanProgress(100);
      
      setPreview(r.emails || []);
      setFilteredPreview(r.emails || []);
      
      if (!silent) {
        setTab('tout');
        const msg = searchKeyword.trim() 
          ? `${r.total_found} email(s) trouvé(s) pour "${searchKeyword}"` 
          : `${r.total_found} email(s) trouvé(s)`;
        setResult({ ok: true, msg });
        
        // Keep 100% visible briefly then hide
        setTimeout(() => {
          setScanProgress(0);
          setLoading(false);
        }, 500);
      }
      setLastSync(new Date().toLocaleTimeString());
    } catch (e) {
      if (progressInterval) clearInterval(progressInterval);
      console.error('Scan error:', e);
      const errorMsg = e.code === 'ECONNABORTED' 
        ? 'Timeout - Le scan prend trop de temps. Réessayez.' 
        : e.response?.data?.error || e.message || 'Erreur lors du scan';
      if (!silent) {
        setResult({ ok: false, msg: errorMsg });
        setLoading(false);
        setScanProgress(0);
      }
    }
    
    if (silent) {
      // Silent mode - just clear loading state
      setLoading(false);
    }
  };

  // Stop scan
  const stopScan = () => {
    setShouldStop(true);
    setLoading(false);
    setScanProgress(0);
    setResult({ ok: false, msg: 'Scan arrêté' });
  };

  useEffect(() => {
    if (open) {
      loadAccounts();
      // Auto-load preview when modal opens - always load fresh emails
      loadPreview(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-sync effect
  useEffect(() => {
    if (autoSync && accounts.length > 0) {
      // Start auto-sync
      autoSyncTimer.current = setInterval(async () => {
        console.log('Auto-sync triggered');
        await loadPreview(true);
      }, autoSyncInterval * 1000);
      
      return () => {
        if (autoSyncTimer.current) {
          clearInterval(autoSyncTimer.current);
        }
      };
    } else {
      // Stop auto-sync
      if (autoSyncTimer.current) {
        clearInterval(autoSyncTimer.current);
        autoSyncTimer.current = null;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSync, autoSyncInterval, accounts.length]);

  // Update filtered preview when preview changes
  useEffect(() => {
    setFilteredPreview(preview);
  }, [preview]);

  const detectProvider = (addr) => {
    const a = addr.toLowerCase();
    if (a.includes('@gmail')) return 'gmail';
    if (a.includes('@yahoo')) return 'yahoo';
    if (a.includes('@hotmail') || a.includes('@outlook') || a.includes('@live')) return 'outlook';
    if (a.includes('@icloud') || a.includes('@me.com')) return 'icloud';
    return null;
  };

  const addAccount = async () => {
    if (!email || !password || !provider) return;
    setLoading(true);
    setResult(null);
    
    try {
      const p = PROVIDERS[provider];
      const r = await ticketService.addEmailAccount({
        email,
        app_password: password,
        imap_server: p.server,
        imap_port: 993,
        provider,
        active: true
      });
      
      if (r.success) {
        setResult({ ok: true, msg: `${email} ajouté !` });
        setEmail('');
        setPassword('');
        setShowAddForm(false);
        loadAccounts();
      } else {
        setResult({ ok: false, msg: r.error || 'Erreur de connexion' });
      }
    } catch (e) {
      setResult({ ok: false, msg: 'Erreur de connexion' });
    }
    setLoading(false);
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Supprimer ce compte email ?')) return;
    try {
      await ticketService.deleteEmailAccount(id);
      toast.success('Compte supprimé');
      loadAccounts();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSelectEmail = (index) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedEmails(newSelected);
  };

  const selectAll = () => {
    if (selectedEmails.size === filteredPreview.length) {
      setSelectedEmails(new Set());
    } else {
      // Get indices of filtered emails in original preview array
      const indices = filteredPreview.map(fe => preview.indexOf(fe));
      setSelectedEmails(new Set(indices));
    }
  };

  const selectFiltered = () => {
    // Select all emails that match the current filter
    const indices = filteredPreview.map(fe => preview.indexOf(fe));
    setSelectedEmails(new Set(indices));
  };

  const [importProgress, setImportProgress] = useState(null);

  const importSelected = async () => {
    if (selectedEmails.size === 0) return;
    
    setImporting(true);
    setResult(null);
    setImportProgress(null);
    
    try {
      // Build list of email IDs to import
      const emailIds = Array.from(selectedEmails).map(i => ({
        account: preview[i].account,
        email_id: preview[i].email_id
      }));
      
      // Progress callback for async mode
      const onProgress = (status) => {
        setImportProgress({
          processed: status.processed,
          total: status.total_emails,
          created: status.created,
          updated: status.updated,
          percent: status.progress_percent,
          current: status.current_email
        });
      };
      
      const r = await ticketService.importSelectedEmails(emailIds, 'WORLD CUP 2026', onProgress);
      
      if (r.success) {
        const created = r.tickets_created || 0;
        const updated = r.tickets_updated || 0;
        
        // Show toast notification
        toast.success(`✅ Import terminé: ${created} créé(s), ${updated} mis à jour`, {
          duration: 4000,
        });
        
        // ALWAYS refresh main data immediately
        if (onRefreshData) {
          onRefreshData();
        }
        
        // Close modal after successful import
        setSelectedEmails(new Set());
        setImportProgress(null);
        setResult(null);
        onOpenChange(false);
        
      } else {
        setResult({ ok: false, msg: r.message || r.error || 'Erreur lors de l\'import' });
        toast.error('Erreur lors de l\'import');
      }
    } catch (e) {
      console.error('Import error:', e);
      setResult({ ok: false, msg: 'Erreur lors de l\'import: ' + (e.message || e) });
      toast.error('Erreur lors de l\'import');
    }
    setImporting(false);
    setImportProgress(null);
  };

  // Import all filtered emails
  const importFiltered = async () => {
    if (filteredPreview.length === 0) return;
    
    // Select all filtered and import
    const indices = filteredPreview.map(fe => preview.indexOf(fe));
    setSelectedEmails(new Set(indices));
    
    setImporting(true);
    setResult(null);
    setImportProgress(null);
    
    try {
      const emailIds = indices.map(i => ({
        account: preview[i].account,
        email_id: preview[i].email_id
      }));
      
      // Progress callback for async mode
      const onProgress = (status) => {
        setImportProgress({
          processed: status.processed,
          total: status.total_emails,
          created: status.created,
          updated: status.updated,
          percent: status.progress_percent,
          current: status.current_email
        });
      };
      
      const r = await ticketService.importSelectedEmails(emailIds, 'WORLD CUP 2026', onProgress);
      
      if (r.success) {
        const created = r.tickets_created || 0;
        const updated = r.tickets_updated || 0;
        
        // Show toast notification
        toast.success(`✅ Import terminé: ${created} créé(s), ${updated} mis à jour`, {
          duration: 4000,
        });
        
        // ALWAYS refresh main data immediately
        if (onRefreshData) {
          onRefreshData();
        }
        
        // Close modal after successful import
        setSelectedEmails(new Set());
        setImportProgress(null);
        setResult(null);
        onOpenChange(false);
        
      } else {
        setResult({ ok: false, msg: r.message || r.error || 'Erreur lors de l\'import' });
        toast.error('Erreur lors de l\'import');
      }
    } catch (e) {
      console.error('Import error:', e);
      setResult({ ok: false, msg: 'Erreur lors de l\'import: ' + (e.message || e) });
      toast.error('Erreur lors de l\'import');
    }
    setImporting(false);
    setImportProgress(null);
  };

  // IMPORT ALL - Import all loaded emails automatically
  const importAll = async () => {
    if (filteredPreview.length === 0) {
      toast.error('Aucun email à importer. Chargez d\'abord les emails.');
      return;
    }
    
    setImporting(true);
    setResult(null);
    setImportProgress(null);
    
    try {
      // Select ALL emails
      const emailIds = filteredPreview.map(em => ({
        account: em.account,
        email_id: em.email_id
      }));
      
      toast.info(`🚀 Import de ${emailIds.length} email(s) en cours...`, { duration: 3000 });
      
      // Progress callback for async mode
      const onProgress = (status) => {
        setImportProgress({
          processed: status.processed,
          total: status.total_emails,
          created: status.created,
          updated: status.updated,
          percent: status.progress_percent,
          current: status.current_email
        });
      };
      
      const r = await ticketService.importSelectedEmails(emailIds, 'WORLD CUP 2026', onProgress);
      
      if (r.success) {
        const created = r.tickets_created || 0;
        const updated = r.tickets_updated || 0;
        
        // Show success toast
        toast.success(`✅ IMPORT TERMINÉ: ${created} créé(s), ${updated} mis à jour`, {
          duration: 5000,
        });
        
        // ALWAYS refresh main data immediately
        if (onRefreshData) {
          onRefreshData();
        }
        
        // Close modal after successful import
        setSelectedEmails(new Set());
        setImportProgress(null);
        setResult(null);
        onOpenChange(false);
        
      } else {
        setResult({ ok: false, msg: r.message || r.error || 'Erreur lors de l\'import' });
        toast.error('Erreur lors de l\'import');
      }
    } catch (e) {
      console.error('Import all error:', e);
      setResult({ ok: false, msg: 'Erreur lors de l\'import: ' + (e.message || e) });
      toast.error('Erreur lors de l\'import');
    }
    setImporting(false);
    setImportProgress(null);
  };

  const prov = provider ? PROVIDERS[provider] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-[#121212] border-[#C4A052]/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-white">
            <Inbox className="h-6 w-6 text-[#C4A052]" />
            Boîtes Mail Connectées
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-zinc-900 rounded-lg">
            <button onClick={() => setTab('tout')}
              className={`flex-1 px-2 py-2 rounded text-sm font-medium ${tab === 'tout' ? 'bg-[#C4A052] text-black' : 'text-zinc-400'}`}>
              <Inbox className="h-4 w-4 inline mr-1" /> TOUT
            </button>
            <button onClick={() => setTab('accounts')}
              className={`flex-1 px-2 py-2 rounded text-sm font-medium ${tab === 'accounts' ? 'bg-[#C4A052] text-black' : 'text-zinc-400'}`}>
              <Mail className="h-4 w-4 inline mr-1" /> Comptes ({accounts.length})
            </button>
            <button onClick={() => loadPreview()}
              className={`flex-1 px-2 py-2 rounded text-sm font-medium ${tab === 'preview' ? 'bg-[#C4A052] text-black' : 'text-zinc-400'}`}>
              <Search className="h-4 w-4 inline mr-1" /> Scanner
            </button>
            <button onClick={() => setTab('settings')}
              className={`flex-1 px-2 py-2 rounded text-sm font-medium ${tab === 'settings' ? 'bg-[#C4A052] text-black' : 'text-zinc-400'}`}>
              <Settings className="h-4 w-4 inline mr-1" /> Config
            </button>
          </div>

          {/* TOUT Tab - All detected emails */}
          {tab === 'tout' && (
            <div className="space-y-4">
              {/* Auto-sync toggle */}
              <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-2">
                  {autoSync ? <Play className="h-4 w-4 text-emerald-400" /> : <Pause className="h-4 w-4 text-zinc-500" />}
                  <span className="text-sm text-white">Mode AUTO</span>
                  {autoSync && lastSync && (
                    <span className="text-xs text-zinc-500">Dernier: {lastSync}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select 
                    value={autoSyncInterval} 
                    onChange={(e) => setAutoSyncInterval(Number(e.target.value))}
                    className="bg-zinc-700 text-white text-xs rounded px-2 py-1 border-none"
                  >
                    <option value={30}>30s</option>
                    <option value={60}>1 min</option>
                    <option value={120}>2 min</option>
                    <option value={300}>5 min</option>
                  </select>
                  <Switch 
                    checked={autoSync} 
                    onCheckedChange={setAutoSync}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>
              </div>

              {/* Date filter */}
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <Input 
                    type="date" 
                    value={dateFilter}
                    onChange={e => setDateFilter(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white h-9"
                    placeholder="Filtrer par date"
                  />
                </div>
                <span className="text-xs text-zinc-500">Date (optionnel)</span>
              </div>

              {/* BIG LOAD BUTTON */}
              <Button
                onClick={() => { setSearchKeyword(''); loadPreview(); }}
                disabled={loading || importing}
                className="w-full h-12 bg-[#C4A052] hover:bg-[#D4B86A] text-black font-bold text-lg"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                    Chargement... {scanProgress > 0 && `${scanProgress}%`}
                  </>
                ) : (
                  <>
                    <Inbox className="h-5 w-5 mr-2" />
                    📬 CHARGER LES EMAILS (48h)
                  </>
                )}
              </Button>

              {/* BIG IMPORT ALL BUTTON - Only show when emails are loaded */}
              {filteredPreview.length > 0 && !loading && (
                <Button
                  onClick={importAll}
                  disabled={importing || loading}
                  data-testid="import-all-btn"
                  className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg animate-pulse"
                >
                  {importing ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Import en cours... {importProgress ? `${importProgress.percent}%` : ''}
                    </>
                  ) : (
                    <>
                      <Download className="h-5 w-5 mr-2" />
                      🚀 IMPORTER TOUT ({filteredPreview.length} emails) → BASE
                    </>
                  )}
                </Button>
              )}

              {/* Search keyword - DIRECT IMAP search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C4A052]" />
                  <Input 
                    type="text" 
                    placeholder="🔍 Rechercher par expéditeur ou sujet..." 
                    value={searchKeyword}
                    onChange={e => setSearchKeyword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadPreview()}
                    className="bg-zinc-800 border-[#C4A052]/50 text-white pl-10 pr-4"
                  />
                </div>
                <Button
                  onClick={() => loadPreview()}
                  disabled={loading || !searchKeyword.trim()}
                  className="bg-zinc-700 hover:bg-zinc-600 text-white"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>

              {/* Progress bar */}
              {loading && scanProgress > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>Recherche en cours...</span>
                    <span>{scanProgress}%</span>
                  </div>
                  <div className="w-full bg-zinc-700 rounded-full h-2">
                    <div 
                      className="bg-[#C4A052] h-2 rounded-full transition-all duration-300"
                      style={{ width: `${scanProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Status and STOP button */}
              <div className="flex items-center justify-between">
                <p className="text-white font-medium">
                  {loading ? (
                    <span className="text-[#C4A052]">Scan en cours...</span>
                  ) : (
                    <>
                      {filteredPreview.length} email(s) trouvé(s)
                      {searchKeyword && <span className="text-[#C4A052] ml-2">pour &quot;{searchKeyword}&quot;</span>}
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  {loading && (
                    <Button 
                      onClick={stopScan}
                      variant="destructive"
                      size="sm"
                      className="bg-red-600 hover:bg-red-500"
                    >
                      <X className="h-4 w-4 mr-1" />
                      STOP
                    </Button>
                  )}
                  {lastSync && (
                    <span className="text-xs text-zinc-500">
                      Dernier: {lastSync}
                    </span>
                  )}
                </div>
              </div>

              {filteredPreview.length > 0 && (
                <div className="flex items-center justify-between border-b border-zinc-700 pb-2">
                  <Button variant="ghost" size="sm" onClick={selectAll} className="text-zinc-400 hover:text-white">
                    {selectedEmails.size === filteredPreview.length && filteredPreview.length > 0 ? (
                      <><CheckSquare className="h-4 w-4 mr-1" /> Tout désélectionner</>
                    ) : (
                      <><Square className="h-4 w-4 mr-1" /> Tout sélectionner ({filteredPreview.length})</>
                    )}
                  </Button>
                  <span className="text-sm text-zinc-500">
                    {selectedEmails.size} sélectionné(s)
                  </span>
                </div>
              )}

              {filteredPreview.length > 0 ? (
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {filteredPreview.map((em, i) => {
                    const originalIndex = preview.indexOf(em);
                    return (
                      <div 
                        key={i} 
                        onClick={() => toggleSelectEmail(originalIndex)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                          selectedEmails.has(originalIndex) 
                            ? 'bg-[#C4A052]/20 border-[#C4A052] shadow-lg shadow-[#C4A052]/10' 
                            : 'bg-zinc-800/50 border-zinc-700 hover:border-[#C4A052]/50 hover:bg-zinc-800'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox 
                            checked={selectedEmails.has(originalIndex)} 
                            onCheckedChange={() => toggleSelectEmail(originalIndex)}
                            className="mt-1 border-[#C4A052]/50 data-[state=checked]:bg-[#C4A052] data-[state=checked]:border-[#C4A052]"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate">{em.subject || '(Sans sujet)'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-[#C4A052]">{em.from}</span>
                              <span className="text-xs text-zinc-600">•</span>
                              <span className="text-xs text-zinc-500">{em.date}</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {em.platform && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium whitespace-nowrap">
                                {em.platform}
                              </span>
                            )}
                            {em.type && (
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                                em.type === 'ACHAT' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                              }`}>
                                {em.type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  {loading ? (
                    <>
                      <Loader2 className="h-12 w-12 mx-auto mb-2 animate-spin opacity-30" />
                      <p>Chargement des emails...</p>
                    </>
                  ) : searchKeyword ? (
                    <>
                      <Filter className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Aucun email trouvé pour &quot;{searchKeyword}&quot;</p>
                    </>
                  ) : (
                    <>
                      <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Aucun email de billet détecté</p>
                      <p className="text-xs mt-1">Cliquez sur &quot;Actualiser&quot; pour scanner vos boîtes mail</p>
                    </>
                  )}
                </div>
              )}

              {/* Import Progress Bar */}
              {importProgress && (
                <div className="space-y-2 p-3 bg-zinc-800/50 rounded-lg border border-emerald-500/50 animate-pulse">
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-400 font-bold">📧 {importProgress.current || `Email ${importProgress.processed}/${importProgress.total}`}</span>
                    <span className="text-[#C4A052] font-bold">{importProgress.percent}%</span>
                  </div>
                  <div className="w-full bg-zinc-700 rounded-full h-3">
                    <div 
                      className="bg-emerald-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress.percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-white">{importProgress.processed} / {importProgress.total} traité(s)</span>
                    <span className="text-emerald-400">✅ {importProgress.created} créé(s) | 🔄 {importProgress.updated} mis à jour</span>
                  </div>
                </div>
              )}

              {/* Import Selected Button */}
              {selectedEmails.size > 0 && (
                <Button 
                  onClick={importSelected} 
                  disabled={importing}
                  data-testid="import-selected-btn"
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold"
                >
                  {importing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {importProgress ? `${importProgress.percent}%` : 'Import en cours...'}</>
                  ) : (
                    <><Download className="h-4 w-4 mr-2" /> Importer {selectedEmails.size} email(s) → TOUT</>
                  )}
                </Button>
              )}

              <p className="text-xs text-emerald-400 bg-emerald-500/10 p-2 rounded">
                ✓ Les billets importés vont dans TOUT et sont automatiquement associés à ACHAT ou VENTE selon leur type.
              </p>
            </div>
          )}

          {/* Accounts Tab */}
          {tab === 'accounts' && (
            <div className="space-y-4">
              {/* Connected accounts */}
              {accounts.length > 0 ? (
                <div className="space-y-2">
                  {accounts.map(acc => (
                    <div key={acc.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{PROVIDERS[acc.provider]?.icon || '📧'}</span>
                        <div>
                          <p className="text-white font-medium">{acc.email}</p>
                          <p className="text-xs text-zinc-500">{PROVIDERS[acc.provider]?.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-emerald-400">● Connecté</span>
                        <Button variant="ghost" size="sm" onClick={() => deleteAccount(acc.id)} 
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Aucun compte connecté</p>
                </div>
              )}

              {/* Add account button/form */}
              {!showAddForm ? (
                <Button onClick={() => setShowAddForm(true)} variant="outline" 
                  className="w-full border-[#C4A052]/30 text-[#C4A052]">
                  <Plus className="h-4 w-4 mr-2" /> Ajouter un compte
                </Button>
              ) : (
                <div className="p-4 rounded-lg border border-[#C4A052]/30 bg-zinc-900/50 space-y-4">
                  <p className="text-white font-medium">Ajouter un compte</p>
                  
                  {/* Provider selection */}
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(PROVIDERS).map(([k, p]) => (
                      <button key={k} onClick={() => setProvider(k)}
                        className={`p-2 rounded border text-center ${provider === k ? 'border-[#C4A052] bg-[#C4A052]/10' : 'border-zinc-700'}`}>
                        <span className="text-xl">{p.icon}</span>
                        <p className="text-xs text-zinc-400 mt-1">{p.name.split('/')[0]}</p>
                      </button>
                    ))}
                  </div>

                  <Input type="email" placeholder="votre@email.com" value={email}
                    onChange={e => { setEmail(e.target.value); const d = detectProvider(e.target.value); if (d) setProvider(d); }}
                    className="bg-[#1a1a1a] border-zinc-700 text-white" />

                  {prov && (
                    <a href={prov.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[#C4A052] text-xs hover:underline">
                      <ExternalLink className="h-3 w-3" /> Créer mot de passe {prov.name}
                    </a>
                  )}

                  <div className="relative">
                    <Input type={showPwd ? 'text' : 'password'} placeholder="Mot de passe application" 
                      value={password} onChange={e => setPassword(e.target.value)}
                      className="bg-[#1a1a1a] border-zinc-700 text-white pr-10" />
                    <button onClick={() => setShowPwd(!showPwd)} 
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => setShowAddForm(false)} variant="outline" className="flex-1 border-zinc-700">
                      Annuler
                    </Button>
                    <Button onClick={addAccount} disabled={loading || !email || !password || !provider}
                      className="flex-1 bg-[#C4A052] hover:bg-[#D4B86A] text-black">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connecter'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Scan button */}
              {accounts.length > 0 && (
                <Button onClick={() => loadPreview()} disabled={loading}
                  className="w-full h-12 bg-[#C4A052] hover:bg-[#D4B86A] text-black font-bold">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Scanner les emails de billets
                </Button>
              )}
            </div>
          )}

          {/* Preview Tab */}
          {tab === 'preview' && (
            <div className="space-y-4">
              {/* Auto-sync toggle */}
              <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-2">
                  {autoSync ? <Play className="h-4 w-4 text-emerald-400" /> : <Pause className="h-4 w-4 text-zinc-500" />}
                  <span className="text-sm text-white">Mode AUTO</span>
                  {autoSync && lastSync && (
                    <span className="text-xs text-zinc-500">Dernier: {lastSync}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select 
                    value={autoSyncInterval} 
                    onChange={(e) => setAutoSyncInterval(Number(e.target.value))}
                    className="bg-zinc-700 text-white text-xs rounded px-2 py-1 border-none"
                  >
                    <option value={30}>30s</option>
                    <option value={60}>1 min</option>
                    <option value={120}>2 min</option>
                    <option value={300}>5 min</option>
                  </select>
                  <Switch 
                    checked={autoSync} 
                    onCheckedChange={setAutoSync}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>
              </div>

              {/* Search/Filter input */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input 
                  type="text" 
                  placeholder="Filtrer par mot-clé (ex: ROCKY CONFIRMATION)" 
                  value={searchKeyword}
                  onChange={e => setSearchKeyword(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white pl-10 pr-20"
                />
                {searchKeyword && filteredPreview.length > 0 && (
                  <Button 
                    size="sm" 
                    onClick={importFiltered}
                    disabled={importing}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 bg-emerald-600 hover:bg-emerald-500 text-xs"
                  >
                    <Zap className="h-3 w-3 mr-1" />
                    Importer ({filteredPreview.length})
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-white font-medium">
                  {filteredPreview.length} email(s) {searchKeyword ? 'trouvé(s)' : 'détecté(s)'}
                  {searchKeyword && preview.length !== filteredPreview.length && (
                    <span className="text-zinc-500 text-sm ml-2">/ {preview.length} total</span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => loadPreview()} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {filteredPreview.length > 0 && (
                <div className="flex items-center justify-between border-b border-zinc-700 pb-2">
                  <Button variant="ghost" size="sm" onClick={selectAll} className="text-zinc-400 hover:text-white">
                    {selectedEmails.size === filteredPreview.length && filteredPreview.length > 0 ? (
                      <><CheckSquare className="h-4 w-4 mr-1" /> Tout désélectionner</>
                    ) : (
                      <><Square className="h-4 w-4 mr-1" /> Tout sélectionner ({filteredPreview.length})</>
                    )}
                  </Button>
                  <span className="text-sm text-zinc-500">
                    {selectedEmails.size} sélectionné(s)
                  </span>
                </div>
              )}

              {filteredPreview.length > 0 ? (
                <div className="max-h-[250px] overflow-y-auto space-y-2">
                  {filteredPreview.map((em, i) => {
                    const originalIndex = preview.indexOf(em);
                    return (
                      <div 
                        key={i} 
                        onClick={() => toggleSelectEmail(originalIndex)}
                        className={`p-3 rounded border cursor-pointer transition-colors ${
                          selectedEmails.has(originalIndex) 
                            ? 'bg-[#C4A052]/20 border-[#C4A052]/50' 
                            : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox 
                            checked={selectedEmails.has(originalIndex)} 
                            onCheckedChange={() => toggleSelectEmail(originalIndex)}
                            className="mt-1 border-zinc-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{em.subject}</p>
                            <p className="text-xs text-zinc-500">{em.from}</p>
                            <p className="text-xs text-zinc-600">{em.date}</p>
                          </div>
                          <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 whitespace-nowrap">
                            {em.platform}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  {searchKeyword ? (
                    <>
                      <Filter className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Aucun email trouvé pour &quot;{searchKeyword}&quot;</p>
                    </>
                  ) : (
                    <>
                      <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Aucun email de billet détecté</p>
                      <p className="text-xs mt-1">Vérifiez que vos comptes sont bien connectés</p>
                    </>
                  )}
                </div>
              )}

              {/* Import Selected Button */}
              {filteredPreview.length > 0 && selectedEmails.size > 0 && (
                <Button 
                  onClick={importSelected} 
                  disabled={importing || selectedEmails.size === 0}
                  data-testid="import-selected-btn"
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold disabled:opacity-50"
                >
                  {importing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Import en cours...</>
                  ) : (
                    <><Download className="h-4 w-4 mr-2" /> Importer {selectedEmails.size} email(s) sélectionné(s)</>
                  )}
                </Button>
              )}

              <p className="text-xs text-amber-400 bg-amber-500/10 p-2 rounded">
                ⚠️ Tapez un mot-clé pour filtrer et importer rapidement. Les emails restent en &quot;non lu&quot;.
              </p>
            </div>
          )}

          {/* Settings Tab - Platform Management */}
          {tab === 'settings' && (
            <PlatformManager />
          )}

          {/* Result */}
          {result && (
            <div className={`flex items-center gap-2 p-3 rounded ${result.ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
              {result.ok ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              <span className="text-sm">{result.msg}</span>
            </div>
          )}

          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full border-zinc-700 text-zinc-400">
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InboxModal;
